import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowUpRight, ArrowRight, Clock, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LanguageContext";
import useJsonLd from "@/hooks/useJsonLd";
import { POSTS, getPostBySlug, getPostIndex } from "@/data/posts";
import { SITE } from "@/data/site";

const ORIGIN =
  typeof window !== "undefined" && window.location
    ? window.location.origin
    : "https://genesisappliancerepair.com";

const mdComponents = {
  h1: ({ node, ...props }) => (
    <h2
      className="font-display text-3xl sm:text-4xl font-semibold text-[#111111] tracking-[-0.02em] mt-14 mb-6 leading-tight"
      {...props}
    />
  ),
  h2: ({ node, ...props }) => (
    <h3
      className="font-display text-2xl sm:text-3xl font-semibold text-[#111111] tracking-[-0.02em] mt-12 mb-4 leading-tight"
      {...props}
    />
  ),
  h3: ({ node, ...props }) => (
    <h4
      className="font-display text-xl font-semibold text-[#111111] mt-10 mb-3"
      {...props}
    />
  ),
  p: ({ node, ...props }) => (
    <p
      className="text-[17px] leading-[1.75] text-[#1F2937] mb-5 font-normal"
      {...props}
    />
  ),
  ul: ({ node, ...props }) => (
    <ul
      className="list-disc pl-6 mb-6 space-y-2 text-[17px] leading-[1.75] text-[#1F2937] marker:text-[#082133]"
      {...props}
    />
  ),
  ol: ({ node, ...props }) => (
    <ol
      className="list-decimal pl-6 mb-6 space-y-2 text-[17px] leading-[1.75] text-[#1F2937] marker:text-[#082133] marker:font-semibold"
      {...props}
    />
  ),
  li: ({ node, ...props }) => <li className="pl-1" {...props} />,
  strong: ({ node, ...props }) => (
    <strong className="font-semibold text-[#111111]" {...props} />
  ),
  a: ({ node, ...props }) => (
    <a
      className="text-[#082133] underline decoration-[#082133]/30 hover:decoration-[#082133] underline-offset-2"
      {...props}
    />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="my-7 border-l-2 border-[#082133] pl-5 italic text-[#4B5563] text-[17px] leading-relaxed"
      {...props}
    />
  ),
};

const BlogPost = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const post = getPostBySlug(slug);

  // Compute related posts BEFORE any conditional return (hooks must be unconditional)
  const idx = getPostIndex(slug);
  const related = post
    ? [POSTS[(idx + 1) % POSTS.length], POSTS[(idx + 2) % POSTS.length]]
    : [];

  // Article + Breadcrumb JSON-LD
  useJsonLd(
    post ? `article-${slug}-${lang}` : null,
    post
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title[lang],
          description: post.excerpt[lang],
          image: post.image,
          datePublished: post.publishedAt,
          dateModified: post.dateModified || post.publishedAt,
          inLanguage: lang === "es" ? "es" : "en",
          author: {
            "@type": "Organization",
            name: "Genesis Appliance Repair",
            url: `${ORIGIN}/`,
          },
          publisher: {
            "@type": "Organization",
            name: "Genesis Appliance Repair",
            url: `${ORIGIN}/`,
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${ORIGIN}/blog/${slug}`,
          },
        }
      : null,
  );

  useJsonLd(
    post ? `breadcrumb-blog-${slug}-${lang}` : null,
    post
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: t("nav.home"),
              item: `${ORIGIN}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: t("nav.blog"),
              item: `${ORIGIN}/blog`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: post.title[lang],
              item: `${ORIGIN}/blog/${slug}`,
            },
          ],
        }
      : null,
  );

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <article data-testid={`page-blog-post-${slug}`} className="bg-[#FAFAFA]">
      {/* HERO */}
      <header className="mx-auto max-w-4xl px-5 sm:px-8 lg:px-12 pt-14 sm:pt-20 pb-10">
        <Link
          to="/blog"
          data-testid="back-to-blog"
          className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-[#082133] font-semibold mb-7 hover:gap-3 transition-all"
        >
          <ArrowRight className="h-3.5 w-3.5 rotate-180" />
          {t("nav.blog")}
        </Link>

        <div className="flex items-center gap-3 text-[12px] uppercase tracking-[0.18em] mb-6">
          <span className="rounded-full bg-[#DCE3DE] text-[#082133] px-3 py-1 font-semibold">
            {post.tag[lang]}
          </span>
          <span className="text-[#4B5563]">{post.date[lang]}</span>
          <span className="text-[#E5E7EB]">·</span>
          <span className="inline-flex items-center gap-1.5 text-[#4B5563] normal-case tracking-normal">
            <Clock className="h-3.5 w-3.5" />
            {post.minutesRead} min
          </span>
        </div>

        <h1 className="font-display font-semibold text-[#111111] text-[36px] sm:text-[48px] lg:text-[56px] leading-[1.06] tracking-[-0.03em]">
          {post.title[lang]}
        </h1>
        <p className="mt-6 text-[19px] leading-relaxed text-[#4B5563] max-w-3xl">
          {post.excerpt[lang]}
        </p>
      </header>

      {/* COVER IMAGE */}
      <div className="mx-auto max-w-5xl px-5 sm:px-8 lg:px-12 mb-12">
        <div className="aspect-[16/9] rounded-[28px] overflow-hidden shadow-[0_30px_60px_-30px_rgba(8,33,51,0.25)]">
          <img
            src={post.image}
            alt={post.title[lang]}
            className="h-full w-full object-cover"
          />
        </div>
      </div>

      {/* BODY */}
      <div className="mx-auto max-w-3xl px-5 sm:px-8 lg:px-12 pb-20">
        <div data-testid="blog-post-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {post.body[lang]}
          </ReactMarkdown>
        </div>

        {/* INLINE CTA */}
        <div className="mt-14 rounded-3xl bg-white border border-[#E5E7EB] p-8 sm:p-10 shadow-[0_20px_40px_-25px_rgba(8,33,51,0.18)]">
          <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#111111] tracking-[-0.02em]">
            {lang === "es"
              ? "¿Listo para que lo revisemos?"
              : "Ready to have it looked at?"}
          </h3>
          <p className="mt-3 text-[16px] text-[#4B5563] leading-relaxed">
            {lang === "es"
              ? "Reserve una visita en menos de dos minutos. Llegamos con piezas OEM y diagnóstico real."
              : "Book a visit in under two minutes. We arrive with OEM parts and real diagnostics."}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/contact" className="inline-flex">
              <Button
                data-testid="blog-post-book-cta"
                className="rounded-full h-12 px-7 bg-[#082133] text-[#FAFAFA] hover:bg-[#0A2D47]"
              >
                {t("common.bookOnline")}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a
              href={SITE.phoneHref}
              data-testid="blog-post-call-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full h-12 px-6 border border-[#082133]/20 bg-white text-[#082133] hover:bg-[#082133]/5 text-[14px] font-medium"
            >
              <Phone className="h-4 w-4" />
              {SITE.phone}
            </a>
          </div>
        </div>
      </div>

      {/* RELATED */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-24">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#111111] tracking-[-0.02em]">
            {lang === "es" ? "Sigue leyendo" : "Keep reading"}
          </h2>
          <Link
            to="/blog"
            data-testid="related-view-all"
            className="text-[#082133] text-sm font-medium inline-flex items-center gap-2 hover:gap-3 transition-all"
          >
            {lang === "es" ? "Ver el blog" : "View blog"}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
          {related.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`related-post-${p.slug}`}
              className="group rounded-3xl bg-white border border-[#E5E7EB] overflow-hidden hover:-translate-y-1 hover:shadow-[0_25px_50px_-20px_rgba(8,33,51,0.15)] transition-all"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title[lang]}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-3 text-[12px] uppercase tracking-[0.18em]">
                  <span className="text-[#082133] font-semibold">
                    {p.tag[lang]}
                  </span>
                  <span className="text-[#E5E7EB]">·</span>
                  <span className="text-[#4B5563]">{p.date[lang]}</span>
                </div>
                <h3 className="mt-3 font-display text-xl font-semibold text-[#111111] leading-snug">
                  {p.title[lang]}
                </h3>
                <p className="mt-3 text-[14px] leading-relaxed text-[#4B5563]">
                  {p.excerpt[lang]}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
};

export default BlogPost;
